import { fetchTokenAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

const defaultApiBaseUrl = "https://api.cyera.io";

const baseUrl = (apiBaseUrl: string | undefined) =>
  (apiBaseUrl || defaultApiBaseUrl).replace(/\/$/, "");

export const cyera = defineConnector({
  identifier: "cyera",
  name: "Cyera",
  tagLine: "Data security and governance",
  logo,
  protocol: "http",
  tags: ["security", "data"],
  defaultUrlPattern: ({ apiBaseUrl }) =>
    `${baseUrl(apiBaseUrl).replace(/\./g, "\\.")}/.*`,
  auth: (app) =>
    fetchTokenAuth({
      request: {
        url: `${baseUrl(app.configuration.apiBaseUrl)}/v1/login`,
        method: "POST",
        jsonBody: {
          clientId: app.secret.clientId,
          secret: app.secret.clientSecret,
        },
        headers: [
          {
            name: "Accept",
            value: "application/json",
          },
        ],
      },
      extract: { from: "jsonPath", paths: ["jwt"] },
      apply: { kind: "header", scheme: "Bearer" },
    }),
  steps: [
    {
      title: "Generate an API token in Cyera",
      items: [
        {
          kind: "markdown",
          content: `1. In [Cyera](https://app.cyera.io), go to **Settings → API Tokens**
2. Click **Generate Token**
3. Fill in a description (for example "3B"), select the roles the token should apply to, and set an expiration
4. Click **Create**

Cyera then shows the client ID and secret key. Copy the secret key now — it is shown only once.`,
        },
      ],
    },
    {
      title: "Enter your credentials",
      items: [
        {
          kind: "select",
          label: "Region",
          required: true,
          defaultValue: defaultApiBaseUrl,
          target: { kind: "configuration", field: "apiBaseUrl" },
          options: [
            { value: "https://api.cyera.io", label: "US" },
            { value: "https://api-eu.cyera.io", label: "EU" },
            { value: "https://api-euw2.cyera.io", label: "EU West 2 (London)" },
            { value: "https://api-ca.cyera.io", label: "Canada" },
            { value: "https://api-ap.cyera.io", label: "Asia Pacific" },
          ],
          helperText: "The Cyera region hosting your tenant.",
        },
        {
          kind: "input",
          label: "Client ID",
          required: true,
          target: { kind: "secrets", field: "clientId" },
        },
        {
          kind: "input",
          label: "Secret key",
          required: true,
          target: { kind: "secrets", field: "clientSecret" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ apiBaseUrl }) =>
      `${baseUrl(apiBaseUrl)}/v1/classifications?limit=1`,
  },
});
